/**
 * AssessmentResultsReveal
 *
 * Shared post-assessment reveal UI used in two contexts:
 *  - "card" variant: compact, inside HeroAssessmentCard for anonymous visitors
 *  - "page" variant: full-width, in SkillsAssessmentPage for logged-in users
 *
 * Shows:
 *  1. Skill level badge (derived from aiExperienceLevel 1–10)
 *  2. First recommended course — fully visible
 *  3. Blurred placeholders for remaining courses + locked overlay
 *  4. CTA — either "Create account" (anonymous) or "View full plan" (authenticated)
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, BookOpen, Clock, ArrowRight } from 'lucide-react';
import { skillsAssessmentService, AssessmentResult, RecommendedCourse } from '../../services/skillsAssessment';

export interface AssessmentResultsRevealProps {
  assessmentId: string;
  extractedData?: AssessmentResult['extractedData'];
  /** Page variant — more whitespace, centred, wider */
  variant?: 'card' | 'page';
  /** Logged-in: callback to navigate to the full plan */
  onViewPlan?: () => void;
  /** Anonymous: href for the "Create account" link (default /register) */
  registerHref?: string;
}

function getSkillLevel(level: number | undefined): { label: string; dots: number; color: string } {
  if (!level || level <= 2) return { label: 'Beginner',           dots: 1, color: '#5b6cff' };
  if (level <= 4)           return { label: 'Foundation Builder', dots: 2, color: '#5b6cff' };
  if (level <= 6)           return { label: 'Developing',         dots: 3, color: '#00c2a8' };
  if (level <= 8)           return { label: 'Proficient',         dots: 4, color: '#00c2a8' };
  return                           { label: 'Advanced',           dots: 5, color: '#ff6b6b' };
}

export const AssessmentResultsReveal: React.FC<AssessmentResultsRevealProps> = ({
  assessmentId,
  extractedData,
  variant = 'card',
  onViewPlan,
  registerHref = '/register',
}) => {
  const [firstCourse, setFirstCourse] = useState<RecommendedCourse | null>(null);
  const [remainingCount, setRemainingCount] = useState<number | null>(null);
  const [isPlanLoading, setIsPlanLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    skillsAssessmentService
      .generateLearningPlan(assessmentId)
      .then((plan) => {
        if (cancelled) return;
        const courses = plan.recommendedCourses || [];
        setFirstCourse(courses[0] || null);
        setRemainingCount(courses.length > 1 ? courses.length - 1 : null);
      })
      .catch(() => {
        // Non-fatal — gated state still renders, just without real course data
      })
      .finally(() => {
        if (!cancelled) setIsPlanLoading(false);
      });

    return () => { cancelled = true; };
  }, [assessmentId]);

  const skillLevel = getSkillLevel(extractedData?.aiExperienceLevel);
  const isPage = variant === 'page';

  return (
    <div className={isPage ? 'space-y-6' : 'space-y-4'}>
      {/* Success confirmation */}
      <div
        className="rounded-xl p-3 border"
        style={{ background: '#f0fdfa', borderColor: '#99f6e4' }}
      >
        <p className="font-montserrat font-semibold text-sm" style={{ color: '#0d9488' }}>
          ✅ Assessment complete — your personalised plan is ready
        </p>
      </div>

      {/* Skill level badge */}
      <div className={`rounded-xl border border-neutral-100 bg-neutral-50 ${isPage ? 'p-6' : 'p-4'}`}>
        <p className="font-montserrat font-bold text-xs uppercase tracking-widest text-neutral-400 mb-3">
          Your AI Level
        </p>
        <div className="flex items-center gap-3 mb-2">
          <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map((dot) => (
              <span
                key={dot}
                className={`rounded-full ${isPage ? 'w-4 h-4' : 'w-3 h-3'}`}
                style={{ background: dot <= skillLevel.dots ? skillLevel.color : '#e5e7eb' }}
              />
            ))}
          </div>
          <span
            className={`font-montserrat font-bold ${isPage ? 'text-xl' : 'text-base'}`}
            style={{ color: skillLevel.color }}
          >
            {skillLevel.label}
          </span>
        </div>
        <p className="text-xs text-neutral-500 font-open-sans">
          We've matched courses specifically to this level and your stated goals.
        </p>
      </div>

      {/* First course — fully visible */}
      <div>
        <p className="font-montserrat font-bold text-xs uppercase tracking-widest text-neutral-400 mb-2">
          Recommended for you
        </p>

        {isPlanLoading ? (
          <div className="border border-neutral-100 rounded-xl p-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-neutral-200 rounded-lg flex-shrink-0" />
              <div className="flex-1">
                <div className="h-3 bg-neutral-200 rounded w-3/4 mb-2" />
                <div className="h-2 bg-neutral-100 rounded w-1/2" />
              </div>
            </div>
          </div>
        ) : firstCourse ? (
          <div
            className={`border rounded-xl ${isPage ? 'p-5' : 'p-4'}`}
            style={{ borderColor: '#c7d2fe', background: '#eef2ff' }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
                style={{ background: '#5b6cff' }}
              >
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-montserrat font-semibold text-neutral-800 leading-tight ${isPage ? 'text-base' : 'text-sm'}`}>
                  {firstCourse.courseTitle}
                </p>
                {firstCourse.reasoning && (
                  <p className="text-xs text-neutral-500 mt-1 font-open-sans line-clamp-2">
                    {firstCourse.reasoning}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <Clock className="w-3 h-3 text-neutral-400" />
                  <span className="text-xs text-neutral-400">
                    {firstCourse.estimatedCompletionWeeks} week{firstCourse.estimatedCompletionWeeks !== 1 ? 's' : ''}
                  </span>
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={{ background: '#dcfce7', color: '#166534' }}
                  >
                    Start here
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Fallback when plan generation fails */
          <div
            className={`border rounded-xl ${isPage ? 'p-5' : 'p-4'}`}
            style={{ borderColor: '#c7d2fe', background: '#eef2ff' }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
                style={{ background: '#5b6cff' }}
              >
                <BookOpen className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="font-montserrat font-semibold text-sm text-neutral-800">
                  AI Foundations for Practical Use
                </p>
                <p className="text-xs text-neutral-500 font-open-sans mt-0.5">
                  Matched to your level and goals
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Blurred remaining courses */}
      <div className="relative rounded-xl overflow-hidden">
        <div className="filter blur-sm pointer-events-none space-y-2 select-none" aria-hidden>
          {[72, 55, 83].map((w, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 border border-neutral-100 rounded-lg bg-white"
            >
              <div className="w-9 h-9 rounded-lg flex-shrink-0 bg-neutral-200" />
              <div className="flex-1">
                <div className="h-3 bg-neutral-200 rounded mb-1" style={{ width: `${w}%` }} />
                <div className="h-2 bg-neutral-100 rounded w-2/5" />
              </div>
            </div>
          ))}
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-white/80 backdrop-blur-[2px]">
          <Lock className="w-6 h-6 mb-1" style={{ color: '#5b6cff' }} />
          <p className="text-sm font-montserrat font-semibold text-neutral-700 text-center px-4">
            {remainingCount != null && remainingCount > 0
              ? `+ ${remainingCount} more course${remainingCount !== 1 ? 's' : ''} matched to you`
              : '+ Your full 90-day roadmap'}
          </p>
        </div>
      </div>

      {/* CTA */}
      {onViewPlan ? (
        <button
          onClick={onViewPlan}
          className={`w-full rounded-full text-white font-montserrat font-semibold text-center transition-opacity hover:opacity-90 flex items-center justify-center gap-2 ${isPage ? 'py-4 text-base' : 'py-3 text-sm'}`}
          style={{ background: 'linear-gradient(135deg, #5b6cff, #00c2a8)' }}
        >
          View My Full Learning Plan
          <ArrowRight className="w-4 h-4" />
        </button>
      ) : (
        <Link
          to={registerHref}
          className={`block w-full rounded-full text-white font-montserrat font-semibold text-center transition-opacity hover:opacity-90 ${isPage ? 'py-4 text-base' : 'py-3 text-sm'}`}
          style={{ background: 'linear-gradient(135deg, #5b6cff, #00c2a8)' }}
        >
          Create My Free Account — Unlock Full Plan →
        </Link>
      )}

      <p className="text-xs text-center text-neutral-400">
        {onViewPlan
          ? 'Your full plan includes courses, a 90-day roadmap and progress tracking'
          : 'No credit card · Takes 30 seconds · Your plan is saved for 24 hours'}
      </p>
    </div>
  );
};

export default AssessmentResultsReveal;
